import { useState, FormEvent } from 'react';

interface FooterProps {
  scrollToSection: (id: string) => void;
}

export default function Footer({ scrollToSection }: FooterProps) {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubscribe = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d6rur544k19g20dvrr6g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#1A3A3A] text-white relative overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute inset-0 opacity-5">
        <svg className="w-full h-full" viewBox="0 0 1200 400">
          <circle cx="200" cy="200" r="100" fill="white" />
          <circle cx="800" cy="100" r="150" fill="white" />
          <circle cx="1000" cy="300" r="80" fill="white" />
        </svg>
      </div>

      {/* Upper Footer */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left Column - Brand */}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-2 mb-6">
              <img 
                src="https://static.readdy.ai/image/efcca1ae64e2adea8f6cbcf3fc677bc7/dcb07f32778ecb4afada9f63cf477c36.png" 
                alt="Good Pitch Roofing" 
                className="h-10 w-auto"
              />
            </div>

            {/* Email Subscription */}
            <form 
              id="newsletter-form"
              data-readdy-form
              onSubmit={handleSubscribe}
              className="flex items-center gap-2 mb-8"
            >
              <input
                type="email"
                name="email"
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1 bg-transparent border-2 border-white/30 text-white placeholder-white/50 px-4 py-3 rounded-lg focus:outline-none focus:border-[#FF6B35] transition-colors"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-12 h-12 bg-[#FF6B35] rounded-full flex items-center justify-center hover:bg-[#ff5722] transition-colors disabled:opacity-50 flex-shrink-0 cursor-pointer"
              >
                <i className="ri-mail-send-line text-white text-lg"></i>
              </button>
            </form>

            {submitStatus === 'success' && (
              <div className="text-green-400 text-sm mb-4">Thanks for subscribing!</div>
            )}
            {submitStatus === 'error' && (
              <div className="text-red-400 text-sm mb-4">Please try again.</div>
            )}

            <h3 className="text-3xl font-serif leading-tight">
              Oklahoma's Most Trusted Roofing Partner
            </h3>
          </div>

          {/* Right Navigation Grid */}
          <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-8">
            {/* Services */}
            <div>
              <h4 className="text-white text-lg font-bold uppercase mb-4">Services</h4>
              <ul className="space-y-3">
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-white/80 hover:text-[#FF6B35] transition-colors text-sm cursor-pointer">
                    Roof Replacement
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-white/80 hover:text-[#FF6B35] transition-colors text-sm cursor-pointer">
                    Insurance Claims
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-white/80 hover:text-[#FF6B35] transition-colors text-sm cursor-pointer">
                    Storm Damage
                  </button>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-white text-lg font-bold uppercase mb-4">Company</h4>
              <ul className="space-y-3">
                <li>
                  <button onClick={() => scrollToSection('about')} className="text-white/80 hover:text-[#FF6B35] transition-colors text-sm cursor-pointer">
                    About Us
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('process')} className="text-white/80 hover:text-[#FF6B35] transition-colors text-sm cursor-pointer">
                    Our Process
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('testimonials')} className="text-white/80 hover:text-[#FF6B35] transition-colors text-sm cursor-pointer">
                    Testimonials
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('blog')} className="text-white/80 hover:text-[#FF6B35] transition-colors text-sm cursor-pointer">
                    Blog
                  </button>
                </li>
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h4 className="text-white text-lg font-bold uppercase mb-4">Resources</h4>
              <ul className="space-y-3">
                <li>
                  <button onClick={() => scrollToSection('contact')} className="text-white/80 hover:text-[#FF6B35] transition-colors text-sm cursor-pointer">
                    Free Inspection
                  </button>
                </li>
                <li>
                  <span className="text-white/80 text-sm">Financing Options</span>
                </li>
                <li>
                  <span className="text-white/80 text-sm">Warranty Info</span>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-white text-lg font-bold uppercase mb-4">Contact</h4>
              <ul className="space-y-3">
                <li className="flex items-center gap-2">
                  <i className="ri-phone-line text-[#FF6B35]"></i>
                  <a href="tel:405-397-2783" className="text-white/80 hover:text-[#FF6B35] transition-colors text-sm cursor-pointer">
                    405-397-2783
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-shield-check-line text-[#FF6B35]"></i>
                  <span className="text-white/80 text-sm">#80007301</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-map-pin-line text-[#FF6B35]"></i>
                  <span className="text-white/80 text-sm">Oklahoma City Metro</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Lower Footer Bar */}
      <div className="relative z-10 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Copyright */}
            <div className="text-white/40 text-xs">
              © 2025 Good Pitch Roofing. All rights reserved.
            </div>

            {/* Social Media */}
            <div className="flex items-center gap-4">
              <a href="#" className="w-10 h-10 rounded-lg border-2 border-white/20 flex items-center justify-center hover:border-[#FF6B35] hover:bg-[#FF6B35]/10 transition-all cursor-pointer">
                <i className="ri-facebook-fill text-white text-lg"></i>
              </a>
              <a href="#" className="w-10 h-10 rounded-lg border-2 border-white/20 flex items-center justify-center hover:border-[#FF6B35] hover:bg-[#FF6B35]/10 transition-all cursor-pointer">
                <i className="ri-instagram-line text-white text-lg"></i>
              </a>
              <a href="#" className="w-10 h-10 rounded-lg border-2 border-white/20 flex items-center justify-center hover:border-[#FF6B35] hover:bg-[#FF6B35]/10 transition-all cursor-pointer">
                <i className="ri-linkedin-fill text-white text-lg"></i>
              </a>
              <a href="#" className="w-10 h-10 rounded-lg border-2 border-white/20 flex items-center justify-center hover:border-[#FF6B35] hover:bg-[#FF6B35]/10 transition-all cursor-pointer">
                <i className="ri-google-fill text-white text-lg"></i>
              </a>
            </div>

            {/* Privacy */}
            <div>
              <a href="#" className="text-white/40 text-xs hover:text-white hover:underline transition-colors cursor-pointer">
                Privacy Policy
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}